// file: src/LandlordPages/LandlordForgotPassword.jsx
import React from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { requestPasswordReset } from "../api/landlordApi";
import { Link } from "react-router-dom";

const BLUE = "#2A7EFA";
const BLUE_LIGHT = "#5AA9FF";

const Schema = Yup.object({
  email: Yup.string()
    .email("Enter a valid email")
    .required("Email is required"),
});

export default function LandlordForgotPassword() {
  const [sent, setSent] = React.useState(false);
  const [error, setError] = React.useState("");

  return (
    <div className="relative min-h-screen bg-white text-slate-900">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div
          className="absolute -top-24 left-1/2 h-64 w-64 -translate-x-1/2 rounded-full blur-3xl"
          style={{ background: `${BLUE}14` }}
        />
        <div
          className="absolute -bottom-24 right-8 h-72 w-72 rounded-full blur-3xl"
          style={{ background: `${BLUE_LIGHT}12` }}
        />
      </div>

      <div className="mx-auto flex min-h-screen max-w-6xl items-center justify-center px-4 py-8">
        <div className="w-full max-w-md rounded-3xl border border-black/[0.06] bg-white/90 p-6 shadow-xl backdrop-blur">
          <h1 className="text-2xl font-bold tracking-tight">Forgot password</h1>
          <p className="mt-1 text-sm text-slate-600">
            Enter your email and we’ll send a password reset link.
          </p>

          {sent ? (
            <div className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
              Check your inbox for a reset link. You can close this tab.
            </div>
          ) : (
            <Formik
              initialValues={{ email: "" }}
              validationSchema={Schema}
              onSubmit={async ({ email }, { setSubmitting }) => {
                setError("");
                try {
                  const url = `${window.location.origin}/landlord/reset-password`;
                  await requestPasswordReset({
                    email: email.trim(),
                    frontendUrl: url,
                  });
                  setSent(true);
                } catch (e) {
                  setError(e?.message || "Failed to request reset.");
                } finally {
                  setSubmitting(false);
                }
              }}
            >
              {({ isSubmitting }) => (
                <Form className="mt-5 space-y-4">
                  <div>
                    <label className="block text-sm font-medium">Email</label>
                    <Field
                      name="email"
                      type="email"
                      placeholder="you@company.com"
                      className="mt-1 w-full rounded-xl border border-black/[0.06] bg-white px-4 py-3 outline-none focus:ring-4"
                    />
                    <ErrorMessage
                      name="email"
                      component="div"
                      className="mt-1 text-xs text-rose-600"
                    />
                  </div>

                  {error && (
                    <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="group relative inline-flex w-full items-center justify-center overflow-hidden rounded-xl bg-[color:var(--blue)] px-4 py-3 font-medium text-white shadow-lg transition active:scale-[0.99] disabled:opacity-60"
                    style={{
                      ["--blue"]: BLUE,
                      boxShadow: `0 12px 26px ${BLUE}33`,
                    }}
                  >
                    <span className="absolute inset-0 -translate-x-full bg-white/15 transition-transform duration-700 group-hover:translate-x-full" />
                    {isSubmitting ? "Sending…" : "Send reset link"}
                  </button>

                  <div className="text-center text-sm">
                    <Link
                      to="/landlord/signin"
                      className="text-[color:var(--blue)] hover:underline"
                      style={{ ["--blue"]: BLUE }}
                    >
                      Back to sign in
                    </Link>
                  </div>
                </Form>
              )}
            </Formik>
          )}
        </div>
      </div>
    </div>
  );
}
